/**
 * Calculate Prices
 *
 * Functions for calculating complete price information from products
 */

import type { ProductInterface, Money } from '../types';
import { getBasePrice, getFinalPrice, getMaximumPrice, getMinimalPrice } from './price';
import { calculatePrice } from './use-price';

/**
 * Calculated prices result
 */
export interface CalculatedPrices {
    maximumPrice: Money;
    minimumPrice: Money;
    minPrice: string;
    maxPrice: string;
    discount: string | null;
    basePrice: string | null;
    price: string;
}

/**
 * Calculate all prices for a product
 *
 * @param selectedProduct - The product to calculate prices for
 * @returns Object containing all price formats
 */
export function calculatePrices(selectedProduct: ProductInterface): CalculatedPrices {
    const finalPrice = getFinalPrice(selectedProduct);
    const productBasePrice = getBasePrice(selectedProduct);
    const minimumPrice = getMinimalPrice(selectedProduct);
    const maximumPrice = getMaximumPrice(selectedProduct);

    const { price, basePrice, discount } = calculatePrice({
        amount: finalPrice?.value ?? 0,
        baseAmount: productBasePrice.value ?? 0,
        currencyCode: finalPrice?.currency ?? 'VND',
    });

    const { price: minPrice } = calculatePrice({
        amount: minimumPrice.value ?? 0,
        currencyCode: minimumPrice.currency ?? 'VND',
    });

    const { price: maxPrice } = calculatePrice({
        amount: maximumPrice.value ?? 0,
        currencyCode: maximumPrice.currency ?? 'VND',
    });

    return {
        maximumPrice,
        minimumPrice,
        minPrice,
        maxPrice,
        discount,
        basePrice,
        price,
    };
}

/**
 * Simple calculate prices result
 */
export interface CalculatePricesResult {
    finalPrice: number;
    originalPrice: number;
    discount: string;
    discountPercent: number;
    basePrice: string;
    price: string;
}

/**
 * Calculate prices with discount info (simplified version)
 */
export const calculatePricesSimple = (product: ProductInterface): CalculatePricesResult => {
    const priceRange = product.price_range;
    const finalPrice = priceRange?.minimum_price?.final_price?.value ?? 0;
    const originalPrice = priceRange?.minimum_price?.regular_price?.value ?? finalPrice;
    const discountValue = originalPrice - finalPrice;
    const discountPercent = originalPrice > 0 ? Math.round((discountValue / originalPrice) * 100) : 0;

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND',
        }).format(value);
    };

    return {
        finalPrice,
        originalPrice,
        discount: discountPercent > 0 ? `${discountPercent}%` : '',
        discountPercent,
        basePrice: formatCurrency(originalPrice),
        price: formatCurrency(finalPrice),
    };
};
